import React, { useRef, useEffect } from "react";
import { Send, MessageCircle } from "lucide-react";
import { COLORS } from "../../constants/colors";
import UserAvatar from "../Avatar/UserAvatar";

const ChatInterface = ({
  items = [],
  selectedItem = null,
  messages = [],
  isLoadingItems = false,
  isLoadingMessages = false,

  title = "Chat",
  emptyStateTitle = "No messages yet",
  emptyStateSubtitle = "Start the conversation!",
  sidebarEmptyStateSubtitle = "No items available",
  inputPlaceholder = "Share your thoughts...",

  onItemSelect = () => {},
  selectedItemKey = "_id",
  onSendMessage = () => {},
  onMessageKeyPress = null,

  renderItem = null,
  renderMessage = null,
  renderHeader = null,

  showBackButton = false,
  onBack = () => {},
  showInfoButton = false,
  onInfoClick = () => {},

  leftColumnTitle = "Items",
  rightColumnTitle = "Chat",
  itemClassName = "",
  selectedItemClassName = "",

  isGroupChat = false,
}) => {
  const messagesEndRef = useRef(null);
  useEffect(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const defaultRenderItem = (item, isSelected) => (
    <div
      key={item[selectedItemKey]}
      onClick={() => onItemSelect(item)}
      className={`p-3 rounded-lg border cursor-pointer transition-all border-gray-200 hover:border-gray-300 ${itemClassName} ${
        isSelected ? selectedItemClassName : ""
      }`}
      style={isSelected ? { borderColor: COLORS.purple, backgroundColor: COLORS.purpleLight } : {}}
    >
      <div className="flex items-center gap-3">
        {item.profile ? (
          <UserAvatar src={item.profile} alt={item.name || "User"} className="w-10 h-10" fallbackClassName="w-10 h-10" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-sm font-semibold">
              {(item.name || "U")
                .split(" ")
                .map(n => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm truncate">{item.name || item.title || item.channelName || "Unnamed"}</h3>
          <p className="text-gray-500 text-xs mt-1 truncate">{item.subtitle || item.description || item.lastMessage || ""}</p>
        </div>
        {item.unread > 0 && (
          <span className="bg-purple-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
            {item.unread}
          </span>
        )}
      </div>
    </div>
  );

  const formatTime = d => {
    const now = new Date();
    const msgDate = new Date(d);
    const diff = (now - msgDate) / 1000;
    if (diff < 10) return "Just now";
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return msgDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const defaultRenderMessage = (msg, i) => {
    const isOwn = msg.type === "sent" || msg.sent || msg.isOwn;
    
    // Extract sender info for group chats only
    const senderName = msg.senderName || (isOwn ? "You" : "Unknown");
    const senderPhoto = msg.senderPhoto;
    const showSenderInfo = msg.showSenderInfo !== false && isGroupChat;
    const showAvatar = isGroupChat;
    
    return (
      <div key={msg.id || i} className={`flex gap-3 ${isOwn ? "flex-row-reverse" : "flex-row"}`}>
        {/* Avatar - only show for group chats */}
        {showAvatar && <UserAvatar src={senderPhoto} alt={senderName} className="w-8 h-8" fallbackClassName="w-8 h-8" />}
        
        {/* Message bubble */}
        <div className={`flex-1 ${isOwn ? "text-right" : ""}`}>
          <div className={`inline-block max-w-xs lg:max-w-md px-4 py-2 ${
            isOwn 
              ? "bg-[#6e28cc] text-white rounded-2xl rounded-tr-md" 
              : "bg-gray-200 text-gray-800 rounded-2xl rounded-tl-md"
          }`}>
            {showSenderInfo && !isOwn && (
              <div className="font-semibold text-gray-900 text-xs mb-1">
                {senderName}
              </div>
            )}
            <p className="text-sm break-words">{msg.text || msg.message}</p>
            <div className={`mt-1 text-[10px] ${isOwn ? "text-white/70" : "text-gray-500"}`}>
              {msg.timestamp ? formatTime(msg.timestamp) : msg.time || "now"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const defaultRenderHeader = () => (
    <div className="p-4 border-b border-gray-300 flex justify-between items-center">
      <h2 className="text-lg font-bold text-gray-900">
        {selectedItem?.name || selectedItem?.title || selectedItem?.channelName || rightColumnTitle}
      </h2>
      {showInfoButton && (
        <button onClick={onInfoClick} className="p-2 rounded-full hover:bg-gray-100 text-gray-600 focus:outline-none">
          ℹ️
        </button>
      )}
    </div>
  );

  const sendMessage = (msg, e) => {
    onMessageKeyPress ? onMessageKeyPress(msg, e) : onSendMessage(msg);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter" && e.target.value.trim()) {
      sendMessage(e.target.value.trim(), e);
      e.target.value = "";
    }
  };

  const handleSendClick = e => {
    const input = e.target.closest(".relative").querySelector("input");
    if (input?.value.trim()) {
      sendMessage(input.value.trim(), e);
      input.value = "";
    }
  };

  const EmptyState = ({ title, subtitle }) => (
    <div className="flex items-center justify-center h-full text-center text-gray-500">
      <div>
        <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col">
      {showBackButton && (
        <div className="hidden sm:flex items-center gap-4 mb-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            ← <span className="text-sm">Back</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>
        </div>
      )}

      <div className="flex flex-col sm:flex-row bg-white border border-gray-400 rounded-lg h-[calc(100vh-200px)]">
        {/* Sidebar */}
        <div className="w-full sm:w-1/3 border-r border-gray-400 p-4 overflow-y-auto">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{leftColumnTitle}</h2>
          <div className="space-y-3">
            {isLoadingItems ? (
              <div className="text-center text-gray-500 py-4">
                <div className="animate-spin h-6 w-6 border-b-2 border-purple-500 mx-auto mb-2 rounded-full"></div>
                <div className="text-sm">Loading chats...</div>
              </div>
            ) : items.length === 0 ? (
              <EmptyState title="👥" subtitle={sidebarEmptyStateSubtitle} />
            ) : (
              items.map(item => {
                const isSelected = selectedItem?.[selectedItemKey] === item[selectedItemKey];
                return renderItem ? renderItem(item, isSelected) : defaultRenderItem(item, isSelected);
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col h-full">
          {selectedItem ? (
            <>
              {renderHeader ? renderHeader(selectedItem) : defaultRenderHeader()}

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div 
                        className="animate-spin h-10 w-10 border-4 rounded-full mx-auto mb-4"
                        style={{ 
                          borderColor: `${COLORS.purple}20`,
                          borderTopColor: COLORS.purple
                        }}
                      ></div>
                      <p className="text-gray-500 text-sm">Loading chat...</p>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <EmptyState title={emptyStateTitle} subtitle={emptyStateSubtitle} />
                ) : (
                  <>
                    {messages.map((m, i) => (renderMessage ? renderMessage(m, i) : defaultRenderMessage(m, i)))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="p-4 border-t border-gray-300">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={inputPlaceholder}
                    className="w-full p-3 border border-gray-300 rounded-lg pr-12 focus:outline-none focus:ring-2"
                    style={{ "--tw-ring-color": COLORS.purple }}
                    onKeyPress={handleKeyPress}
                  />
                  <button
                    onClick={handleSendClick}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 text-white rounded-full flex items-center justify-center hover:opacity-90"
                    style={{ backgroundColor: COLORS.purple }}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <EmptyState title="Select someone to start chatting" subtitle="Choose a person from the list to begin your conversation" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
